import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Calendar, Clock, Car, User, Phone, Mail, MapPin, ArrowRight, CheckCircle, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { useToast } from '../hooks/use-toast';
import { useAPI, useAPIMutation } from '../hooks/useAPI';
import { pricingAPI, locationsAPI, bookingsAPI } from '../services/api';
import emailjs from '@emailjs/browser';

// Configuration EmailJS
const EMAILJS_SERVICE_ID = 'service_t96nhl9';
const EMAILJS_TEMPLATE_ID = 'template_giug2k2';
const EMAILJS_PUBLIC_KEY = 'fcFKtJepci4Bk3alr';

// Convertit une chaîne de durée (ex: "1h - 1h30", "45 min", "30min - 45min") en minutes (valeur max)
const segmentToMinutes = (seg) => {
  const s = seg.toLowerCase();
  const hMatch = s.match(/(\d+)\s*h\s*(\d+)?/);
  if (hMatch) {
    return parseInt(hMatch[1], 10) * 60 + (hMatch[2] ? parseInt(hMatch[2], 10) : 0);
  }
  const minMatch = s.match(/(\d+)\s*min/);
  if (minMatch) return parseInt(minMatch[1], 10);
  return 0;
};

const parseDurationMinutes = (str) => {
  if (!str) return 60;
  const parts = String(str).split(/[-–]/);
  let max = 0;
  for (const p of parts) {
    const m = segmentToMinutes(p);
    if (m > max) max = m;
  }
  return max || 60;
};

const BookingPage = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [availabilitySlots, setAvailabilitySlots] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [formData, setFormData] = useState({
    service: '',           // Nom du service principal sélectionné
    selectedOptions: [],   // Options à la carte sélectionnées (tableau d'IDs)
    optionQuantities: {},  // Quantités pour les options (ex: { "optionId": 4 })
    includeBookPhoto: false, // Option Book Photo pour Lavage Concession
    location: '',
    date: '',
    time: '',
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    vehicleBrand: '',
    vehicleModel: '',
    vehicleYear: '',
    notes: ''
  });

  // Options qui nécessitent une quantité
  const isQuantityOption = (service) => {
    return service?.toLowerCase().includes('siège') || 
           service?.toLowerCase().includes('jante');
  };

  // API calls - récupérer les tarifs au lieu des services
  const { 
    data: allPricing, 
    loading: pricingLoading, 
    error: pricingError 
  } = useAPI(() => pricingAPI.getAll());

  const { 
    data: locations, 
    loading: locationsLoading, 
    error: locationsError 
  } = useAPI(() => locationsAPI.getBookingLocations());

  // Organiser les tarifs par catégorie
  const simpleServices = allPricing?.filter(p => p.category === 'simple')?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  const principalServices = allPricing?.filter(p => p.category === 'principal')?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  const bookPhotoOption = allPricing?.find(p => p.service?.includes('Book Photo')) || null;
  
  // Tous les services réservables (simples + principaux)
  const bookableServices = [...simpleServices, ...principalServices];
  
  // Vérifier si c'est le Lavage Concession (pour filtrer les options)
  const isLavageConcession = formData.service?.toLowerCase().includes('concession');
  
  // Filtrer les options selon le service sélectionné
  // Pour Lavage Concession: seulement vapeur, cuir, coffre sale
  // Pour les autres: toutes les options sauf Book Photo
  const optionsServices = allPricing?.filter(p => {
    if (p.category !== 'options') return false;
    if (p.service?.includes('Book Photo')) return false; // Book Photo géré séparément
    if (p.restriction) return false;
    
    // Si Lavage Concession, exclure les options restreintes
    if (isLavageConcession && p.restrictedFromConcession) return false;
    
    return true;
  })?.sort((a, b) => (a.order || 0) - (b.order || 0)) || [];

  // Vérifier si un service est pré-sélectionné dans l'URL
  useEffect(() => {
    const preSelectedService = searchParams.get('service');
    if (preSelectedService && allPricing) {
      const serviceName = decodeURIComponent(preSelectedService);
      // Vérifier que ce service existe dans nos données
      const serviceExists = bookableServices.some(s => s.service === serviceName);
      if (serviceExists) {
        setFormData(prev => ({
          ...prev,
          service: serviceName
        }));
        // Passer directement à l'étape 2 si un service est pré-sélectionné
        setStep(2);
      }
    }
  }, [searchParams, allPricing]);

  // Mutation pour créer la réservation
  const { mutate: createBooking, loading: bookingLoading } = useAPIMutation();

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle une option à la carte
  const toggleOption = (optionId, optionService) => {
    setFormData(prev => {
      const currentOptions = prev.selectedOptions || [];
      const currentQuantities = prev.optionQuantities || {};
      
      if (currentOptions.includes(optionId)) {
        // Désélectionner l'option et supprimer la quantité
        const newQuantities = { ...currentQuantities };
        delete newQuantities[optionId];
        return { 
          ...prev, 
          selectedOptions: currentOptions.filter(id => id !== optionId),
          optionQuantities: newQuantities
        };
      } else {
        // Sélectionner l'option avec quantité par défaut si nécessaire
        const newQuantities = { ...currentQuantities };
        if (isQuantityOption(optionService)) {
          newQuantities[optionId] = 1; // Quantité par défaut
        }
        return { 
          ...prev, 
          selectedOptions: [...currentOptions, optionId],
          optionQuantities: newQuantities
        };
      }
    });
  };

  // Modifier la quantité d'une option
  const updateOptionQuantity = (optionId, quantity) => {
    const newQty = Math.max(1, Math.min(10, quantity)); // Entre 1 et 10
    setFormData(prev => ({
      ...prev,
      optionQuantities: {
        ...prev.optionQuantities,
        [optionId]: newQty
      }
    }));
  };

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Calculer le prix total
  const calculateTotal = () => {
    let total = 0;
    
    // Service principal
    const mainService = bookableServices.find(s => s.service === formData.service);
    if (mainService && mainService.price) {
      const priceMatch = mainService.price.match(/(\d+)/);
      if (priceMatch) total += parseInt(priceMatch[1]);
    }
    
    // Options sélectionnées (avec quantités)
    formData.selectedOptions.forEach(optId => {
      const option = optionsServices.find(o => o.id === optId);
      if (option && option.price) {
        const priceMatch = option.price.match(/(\d+)/);
        if (priceMatch) {
          const unitPrice = parseInt(priceMatch[1]);
          const quantity = formData.optionQuantities[optId] || 1;
          total += unitPrice * quantity;
        }
      }
    });
    
    // Book Photo
    if (formData.includeBookPhoto && bookPhotoOption) {
      const priceMatch = bookPhotoOption.price.match(/(\d+)/);
      if (priceMatch) total += parseInt(priceMatch[1]);
    }
    
    return total;
  };

  // Durée totale (en minutes) du service + options sélectionnés
  const calculateDuration = () => {
    let minutes = 0;
    const mainService = bookableServices.find(s => s.service === formData.service);
    if (mainService) minutes += parseDurationMinutes(mainService.duration);
    formData.selectedOptions.forEach(optId => {
      const option = optionsServices.find(o => o.id === optId);
      if (option) {
        const qty = formData.optionQuantities[optId] || 1;
        minutes += parseDurationMinutes(option.duration) * qty;
      }
    });
    if (formData.includeBookPhoto && bookPhotoOption) {
      minutes += parseDurationMinutes(bookPhotoOption.duration);
    }
    return minutes || 60;
  };

  // Récupérer les créneaux disponibles quand la date/le service/les options changent
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!formData.date || !formData.service) {
        setAvailabilitySlots(null);
        return;
      }
      setAvailabilityLoading(true);
      try {
        const duration = calculateDuration();
        const res = await bookingsAPI.getAvailability(formData.date, duration);
        const slots = res.data.slots || [];
        setAvailabilitySlots(slots);
        // Réinitialiser le créneau si celui choisi n'est plus disponible
        const chosen = slots.find(s => s.time === formData.time);
        if (formData.time && (!chosen || !chosen.available)) {
          setFormData(prev => ({ ...prev, time: '' }));
        }
      } catch (e) {
        setAvailabilitySlots(null);
      } finally {
        setAvailabilityLoading(false);
      }
    };
    fetchAvailability();
  }, [formData.date, formData.service, JSON.stringify(formData.selectedOptions), JSON.stringify(formData.optionQuantities), formData.includeBookPhoto]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Récupérer les détails des options sélectionnées (avec quantités)
      const selectedOptionDetails = formData.selectedOptions.map(optId => {
        const opt = optionsServices.find(o => o.id === optId);
        if (!opt) return null;
        const quantity = formData.optionQuantities[optId] || 1;
        const priceMatch = opt.price.match(/(\d+)/);
        const unitPrice = priceMatch ? parseInt(priceMatch[1]) : 0;
        return { 
          id: opt.id, 
          name: opt.service, 
          price: opt.price,
          quantity: quantity,
          subtotal: unitPrice * quantity
        };
      }).filter(Boolean);

      const bookingData = {
        serviceName: formData.service,
        selectedOptions: selectedOptionDetails,
        includeBookPhoto: formData.includeBookPhoto,
        totalEstimated: calculateTotal(),
        durationMinutes: calculateDuration(),
        locationId: formData.location,
        date: formData.date,
        time: formData.time,
        client: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          email: formData.email
        },
        vehicle: {
          brand: formData.vehicleBrand || null,
          model: formData.vehicleModel || null,
          year: formData.vehicleYear ? parseInt(formData.vehicleYear) : null
        },
        notes: formData.notes || null
      };

      // Sauvegarder en base de données (étape critique)
      try {
        await createBooking(() => bookingsAPI.create(bookingData));
      } catch (bookingError) {
        console.error('Erreur enregistrement réservation:', bookingError);
        const detail = bookingError.response?.data?.detail;
        toast({
          title: "Réservation impossible",
          description: detail || "Une erreur est survenue lors de l'enregistrement. Veuillez réessayer.",
          variant: "destructive",
        });
        // Si le créneau n'est plus disponible, revenir à l'étape date/heure
        if (bookingError.response?.status === 409) {
          setStep(2);
          setFormData(prev => ({ ...prev, time: '' }));
        }
        return;
      }

      // Préparer les options pour l'email (avec quantités)
      const optionsText = selectedOptionDetails.length > 0 
        ? selectedOptionDetails.map(o => {
            if (o.quantity > 1) {
              return `${o.name} x${o.quantity} (${o.subtotal}€)`;
            }
            return `${o.name} (${o.price})`;
          }).join(', ')
        : 'Aucune';
      const bookPhotoText = formData.includeBookPhoto ? ' + Book Photo (59€)' : '';
      
      // Notification email via EmailJS.
      // La réservation est déjà enregistrée : un échec d'envoi d'email ne doit PAS
      // bloquer la confirmation ni faire croire au client que sa réservation a échoué.
      const emailParams = {
        service: formData.service,
        options: optionsText + bookPhotoText,
        total: calculateTotal(),
        date: new Date(formData.date).toLocaleDateString('fr-FR'),
        time: formData.time,
        location: getSelectedLocation()?.name || 'Non spécifié',
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        vehicleBrand: formData.vehicleBrand || 'Non spécifié',
        vehicleModel: formData.vehicleModel || '',
        vehicleYear: formData.vehicleYear || '',
        notes: formData.notes || 'Aucune note'
      };

      let emailSent = true;
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          emailParams,
          EMAILJS_PUBLIC_KEY
        );
      } catch (emailError) {
        emailSent = false;
        console.error("Échec de l'envoi de l'email de réservation:", emailError);
      }
      
      toast({
        title: "Réservation confirmée !",
        description: emailSent
          ? "Votre demande de réservation a été envoyée. Nous vous contacterons sous 24h pour confirmer."
          : "Votre réservation a bien été enregistrée. Nous vous contacterons sous 24h pour confirmer.",
      });
      
      // Passer à l'étape de confirmation
      setStep(5);
      
    } catch (error) {
      console.error('Erreur:', error);
      toast({
        title: "Erreur lors de la réservation",
        description: error.message || "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    }
  };

  const getSelectedServiceData = () => {
    return bookableServices.find(s => s.service === formData.service);
  };

  const getSelectedLocation = () => {
    return locations?.find(l => l.id === formData.location);
  };

  // Loading state pour les données essentielles
  if (pricingLoading || locationsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Chargement du formulaire de réservation...</p>
        </div>
      </div>
    );
  }

  // Error state pour les données essentielles
  if (pricingError || locationsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorMessage 
          error={pricingError || locationsError} 
          onRetry={() => window.location.reload()} 
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Réservation en ligne
          </h1>
          <p className="text-lg text-gray-600">
            Réservez votre créneau en quelques minutes
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[1, 2, 3, 4].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 font-semibold
                  ${step >= stepNum 
                    ? 'bg-blue-600 border-blue-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-500'
                  }`}
                >
                  {step > stepNum ? <CheckCircle className="w-5 h-5" /> : stepNum}
                </div>
                {stepNum < 4 && (
                  <div className={`h-1 w-16 mx-2 
                    ${step > stepNum ? 'bg-blue-600' : 'bg-gray-300'}`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between max-w-2xl mx-auto mt-2 text-sm text-gray-600">
            <span>Service</span>
            <span>Date & Lieu</span>
            <span>Vos infos</span>
            <span>Confirmation</span>
          </div>
        </div>

        <Card className="max-w-2xl mx-auto">
          <CardContent className="p-8">
            {/* Step 1: Service Selection */}
            {step === 1 && (
              <div>
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="flex items-center space-x-2">
                    <Car className="w-6 h-6 text-blue-600" />
                    <span>Choisissez votre service</span>
                  </CardTitle>
                </CardHeader>
                <div className="space-y-6">
                  {/* Lavages Simples */}
                  {simpleServices.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Lavages Simples</h3>
                      <div className="space-y-3">
                        {simpleServices.map((service) => (
                          <div 
                            key={service.id}
                            data-testid={`service-card-${service.id}`}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 
                              ${formData.service === service.service 
                                ? 'border-blue-600 bg-blue-50' 
                                : 'border-gray-200 hover:border-blue-300'
                              }`}
                            onClick={() => handleInputChange('service', service.service)}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-semibold text-gray-900">{service.service}</h4>
                                <p className="text-sm text-gray-600 mt-1">{service.duration}</p>
                              </div>
                              <Badge className="bg-blue-600 text-white">
                                {service.price}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Formules Principales */}
                  {principalServices.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Formules Principales</h3>
                      <div className="space-y-3">
                        {principalServices.map((service) => (
                          <div 
                            key={service.id}
                            data-testid={`service-card-${service.id}`}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 
                              ${formData.service === service.service 
                                ? 'border-blue-600 bg-blue-50' 
                                : 'border-gray-200 hover:border-blue-300'
                              }`}
                            onClick={() => handleInputChange('service', service.service)}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-semibold text-gray-900">{service.service}</h4>
                                <p className="text-sm text-gray-600 mt-1">{service.duration}</p>
                                {service.includes && service.includes.length > 0 && (
                                  <ul className="mt-2 space-y-1">
                                    {service.includes.slice(0, 3).map((item, idx) => (
                                      <li key={idx} className="text-xs text-gray-500 flex items-center">
                                        <CheckCircle className="w-3 h-3 text-green-500 mr-1" />
                                        {item}
                                      </li>
                                    ))}
                                    {service.includes.length > 3 && (
                                      <li className="text-xs text-gray-400">+ {service.includes.length - 3} autres...</li>
                                    )}
                                  </ul>
                                )}
                              </div>
                              <Badge className="bg-blue-600 text-white">
                                {service.price}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-end mt-6">
                  <Button 
                    data-testid="step1-continue-btn"
                    onClick={handleNext}
                    disabled={!formData.service}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Continuer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Date, Location and Options */}
            {step === 2 && (
              <div>
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-6 h-6 text-blue-600" />
                      <span>Date, lieu et options</span>
                    </div>
                    {formData.service && (
                      <Badge className="bg-blue-100 text-blue-800">
                        {getSelectedServiceData()?.price || ''}
                      </Badge>
                    )}
                  </CardTitle>
                  {formData.service && (
                    <p className="text-sm text-gray-600 mt-2">
                      Service sélectionné : <strong>{formData.service}</strong>
                      <button 
                        onClick={() => setStep(1)}
                        className="ml-4 text-blue-600 hover:text-blue-800 underline"
                      >
                        Changer de service
                      </button>
                    </p>
                  )}
                </CardHeader>
                <div className="space-y-6">
                  {/* Date et heure */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="date">Date souhaitée</Label>
                      <Input
                        id="date"
                        data-testid="booking-date-input"
                        type="date"
                        value={formData.date}
                        onChange={(e) => handleInputChange('date', e.target.value)}
                        min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Réservation possible au minimum 24h à l'avance.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="time">Créneau horaire</Label>
                      <select 
                        id="time"
                        data-testid="booking-time-select"
                        value={formData.time} 
                        onChange={(e) => handleInputChange('time', e.target.value)}
                        disabled={!formData.date || availabilityLoading}
                        className="mt-1 flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60"
                      >
                        <option value="">
                          {!formData.date
                            ? "Choisissez d'abord une date"
                            : (availabilityLoading ? "Chargement des disponibilités..." : "Choisir un horaire")}
                        </option>
                        {formData.date && !availabilityLoading && timeSlots.map((time) => {
                          const slot = availabilitySlots?.find(s => s.time === time);
                          const isAvailable = slot ? slot.available : true;
                          let suffix = '';
                          if (slot && !slot.available) {
                            if (slot.reason === 'creneau_occupe') suffix = ' (complet)';
                            else if (slot.reason === 'moins_24h') suffix = ' (trop tôt)';
                            else if (slot.reason === 'duree_depasse_fermeture') suffix = ' (durée trop longue)';
                            else suffix = ' (indisponible)';
                          }
                          return (
                            <option key={time} value={time} disabled={!isAvailable}>
                              {time}{suffix}
                            </option>
                          );
                        })}
                      </select>
                      {formData.date && !availabilityLoading && availabilitySlots && availabilitySlots.every(s => !s.available) && (
                        <p className="text-sm text-red-600 mt-1" data-testid="no-slot-message">
                          Aucun créneau disponible ce jour. Merci de choisir une autre date.
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Centre */}
                  <div>
                    <Label htmlFor="location">Centre AutoSpa 77</Label>
                    <select 
                      id="location"
                      data-testid="booking-location-select"
                      value={formData.location} 
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="mt-1 flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="">Choisir un centre</option>
                      {locations?.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                    {formData.location && getSelectedLocation() && (
                      <p className="text-sm text-gray-600 mt-1">
                        {getSelectedLocation().address}
                      </p>
                    )}
                  </div>

                  {/* Option Book Photo pour Lavage Concession */}
                  {isLavageConcession && bookPhotoOption && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            id="bookPhoto"
                            data-testid="book-photo-checkbox"
                            checked={formData.includeBookPhoto}
                            onChange={(e) => handleInputChange('includeBookPhoto', e.target.checked)}
                            className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <label htmlFor="bookPhoto" className="cursor-pointer">
                            <span className="font-semibold text-gray-900">📸 {bookPhotoOption.service}</span>
                            <p className="text-sm text-gray-600">Photos professionnelles de votre véhicule</p>
                          </label>
                        </div>
                        <Badge className="bg-yellow-600 text-white">{bookPhotoOption.price}</Badge>
                      </div>
                    </div>
                  )}

                  {/* Options à la carte */}
                  {optionsServices.length > 0 && (
                    <div className="border-t pt-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                        <Plus className="w-5 h-5 mr-2 text-blue-600" />
                        Options à la carte (facultatif)
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Personnalisez votre prestation avec des options supplémentaires
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {optionsServices.map((option) => {
                          const isSelected = formData.selectedOptions.includes(option.id);
                          const needsQuantity = isQuantityOption(option.service);
                          const quantity = formData.optionQuantities[option.id] || 1;
                          
                          return (
                            <div 
                              key={option.id}
                              data-testid={`option-card-${option.id}`}
                              className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                                isSelected 
                                  ? 'border-green-500 bg-green-50' 
                                  : 'border-gray-200 hover:border-green-300'
                              }`}
                            >
                              <div 
                                className="flex items-center justify-between cursor-pointer"
                                onClick={() => toggleOption(option.id, option.service)}
                              >
                                <div className="flex items-center space-x-2">
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                    isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'
                                  }`}>
                                    {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                                  </div>
                                  <span className="text-sm font-medium text-gray-900">{option.service}</span>
                                </div>
                                <Badge variant="outline" className="text-green-600 border-green-600">
                                  {option.price}
                                </Badge>
                              </div>
                              
                              {/* Sélecteur de quantité pour sièges/jantes */}
                              {isSelected && needsQuantity && (
                                <div className="mt-3 pt-3 border-t border-green-200">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-700">Quantité :</span>
                                    <div className="flex items-center space-x-2">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateOptionQuantity(option.id, quantity - 1);
                                        }}
                                        className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-700 font-bold"
                                      >
                                        <Minus className="w-4 h-4" />
                                      </button>
                                      <span className="w-8 text-center font-semibold text-lg">{quantity}</span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateOptionQuantity(option.id, quantity + 1);
                                        }}
                                        className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-700 font-bold"
                                      >
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Sous-total : {parseInt(option.price.match(/(\d+)/)?.[1] || 0) * quantity}€
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Résumé prix */}
                  {(formData.selectedOptions.length > 0 || formData.includeBookPhoto) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Estimation du prix</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>{formData.service}</span>
                          <span>{getSelectedServiceData()?.price}</span>
                        </div>
                        {formData.selectedOptions.map(optId => {
                          const opt = optionsServices.find(o => o.id === optId);
                          if (!opt) return null;
                          const qty = formData.optionQuantities[optId] || 1;
                          const unitPrice = parseInt(opt.price.match(/(\d+)/)?.[1] || 0);
                          const subtotal = unitPrice * qty;
                          return (
                            <div key={optId} className="flex justify-between text-gray-600">
                              <span>+ {opt.service} {qty > 1 ? `x${qty}` : ''}</span>
                              <span>{subtotal}€</span>
                            </div>
                          );
                        })}
                        {formData.includeBookPhoto && bookPhotoOption && (
                          <div className="flex justify-between text-gray-600">
                            <span>+ Book Photo</span>
                            <span>{bookPhotoOption.price}</span>
                          </div>
                        )}
                        <div className="border-t pt-2 mt-2 flex justify-between font-bold text-blue-900">
                          <span>Total estimé</span>
                          <span>{calculateTotal()}€</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-between mt-6">
                  <Button onClick={handleBack} variant="outline">
                    Retour
                  </Button>
                  <Button 
                    data-testid="step2-continue-btn"
                    onClick={handleNext}
                    disabled={!formData.date || !formData.time || !formData.location}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Continuer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Personal Information */}
            {step === 3 && (
              <div>
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="flex items-center space-x-2">
                    <User className="w-6 h-6 text-blue-600" />
                    <span>Vos informations</span>
                  </CardTitle>
                </CardHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">Prénom *</Label>
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Nom *</Label>
                      <Input
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="phone">Téléphone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="vehicleBrand">Marque du véhicule</Label>
                      <Input
                        id="vehicleBrand"
                        value={formData.vehicleBrand}
                        onChange={(e) => handleInputChange('vehicleBrand', e.target.value)}
                        placeholder="Ex: Renault"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="vehicleModel">Modèle</Label>
                      <Input
                        id="vehicleModel"
                        value={formData.vehicleModel}
                        onChange={(e) => handleInputChange('vehicleModel', e.target.value)}
                        placeholder="Ex: Clio"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="vehicleYear">Année</Label>
                      <Input
                        id="vehicleYear"
                        type="number"
                        value={formData.vehicleYear}
                        onChange={(e) => handleInputChange('vehicleYear', e.target.value)}
                        placeholder="2020"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes">Notes ou demandes spéciales</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      placeholder="Précisions sur l'état du véhicule, demandes particulières..."
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="flex justify-between mt-6">
                  <Button onClick={handleBack} variant="outline">
                    Retour
                  </Button>
                  <Button 
                    onClick={handleNext}
                    disabled={!formData.firstName || !formData.lastName || !formData.phone || !formData.email}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Continuer
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Confirmation */}
            {step === 4 && (
              <div>
                <CardHeader className="px-0 pt-0">
                  <CardTitle className="flex items-center space-x-2">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                    <span>Confirmation de votre réservation</span>
                  </CardTitle>
                </CardHeader>
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-3">Récapitulatif</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Service principal :</span>
                        <span className="font-medium">{formData.service}</span>
                      </div>
                      {formData.selectedOptions.length > 0 && (
                        <div className="border-t pt-2 mt-2">
                          <span className="text-gray-600">Options sélectionnées :</span>
                          <ul className="mt-1 space-y-1">
                            {formData.selectedOptions.map(optId => {
                              const opt = optionsServices.find(o => o.id === optId);
                              if (!opt) return null;
                              const qty = formData.optionQuantities[optId] || 1;
                              const unitPrice = parseInt(opt.price.match(/(\d+)/)?.[1] || 0);
                              const subtotal = unitPrice * qty;
                              return (
                                <li key={optId} className="flex justify-between text-gray-700 pl-4">
                                  <span>• {opt.service} {qty > 1 ? `x${qty}` : ''}</span>
                                  <span>{subtotal}€</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                      {formData.includeBookPhoto && bookPhotoOption && (
                        <div className="flex justify-between text-gray-700">
                          <span>📸 Book Photo :</span>
                          <span>{bookPhotoOption.price}</span>
                        </div>
                      )}
                      <div className="border-t pt-2 mt-2 flex justify-between font-bold text-blue-600">
                        <span>Total estimé :</span>
                        <span>{calculateTotal()}€</span>
                      </div>
                      <div className="border-t pt-2 mt-2"></div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date :</span>
                        <span className="font-medium">{new Date(formData.date).toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Heure :</span>
                        <span className="font-medium">{formData.time}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centre :</span>
                        <span className="font-medium">{getSelectedLocation()?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Client :</span>
                        <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Contact :</span>
                        <span className="font-medium">{formData.phone}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Important :</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Cette demande de réservation doit être confirmée par nos équipes</li>
                      <li>• Vous recevrez un appel sous 24h pour valider votre créneau</li>
                      <li>• Un acompte pourra être demandé pour certains services</li>
                      <li>• En cas d'empêchement, merci de nous prévenir 24h à l'avance</li>
                    </ul>
                  </div>
                </div>
                <div className="flex justify-between mt-6">
                  <Button onClick={handleBack} variant="outline" disabled={bookingLoading}>
                    Retour
                  </Button>
                  <Button 
                    data-testid="confirm-booking-btn"
                    onClick={handleSubmit}
                    disabled={bookingLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {bookingLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Confirmer la réservation
                        <CheckCircle className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 5: Success */}
            {step === 5 && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Réservation envoyée !
                </h2>
                <p className="text-gray-600 mb-6">
                  Merci pour votre confiance. Nous vous contacterons sous 24h pour confirmer votre rendez-vous.
                </p>
                <div className="space-y-3">
                  <p className="text-sm text-gray-500">
                    En cas de question, contactez-nous au <strong>06 22 36 01 90</strong>
                  </p>
                  <Button 
                    onClick={() => window.location.href = '/'}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Retour à l'accueil
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingPage;